import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api, { getMe, loginWithApple, loginWithGoogle, setUnauthorizedHandler } from '../api/client';

// User type
export interface AuthUser {
  id: string;
  email: string;
  role: 'admin' | 'user';
  is_active: boolean;
  is_verified: boolean;
  subscription_status?: string | null;
  // Server-computed: no live subscription (and billing is enforced).
  needs_subscription?: boolean;
}

// Auth context type
interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  socialLogin: (
    provider: 'google' | 'apple',
    idToken: string,
    fullName?: string
  ) => Promise<void>;
  logout: () => void;
  updateUser: (patch: Partial<AuthUser>) => void;
  /** Re-fetch /auth/me (e.g. after Stripe checkout) and return the fresh user. */
  refreshUser: () => Promise<AuthUser | null>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

function toAuthUser(me: {
  id: string;
  email: string;
  role?: string;
  is_active: boolean;
  is_verified: boolean;
  subscription_status?: string | null;
  needs_subscription?: boolean;
}): AuthUser {
  return {
    id: me.id,
    email: me.email,
    role: me.role === 'admin' ? 'admin' : 'user',
    is_active: me.is_active,
    is_verified: me.is_verified,
    subscription_status: me.subscription_status ?? null,
    needs_subscription: !!me.needs_subscription,
  };
}

// Local storage keys
const TOKEN_KEY = 'solaniq_token';
const USER_KEY = 'solaniq_user';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // When a request comes back 401 mid-session, clear React auth state so
  // ProtectedRoute redirects through the SPA router (the api client already
  // cleared localStorage). This replaces the old hard `window.location` reload.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setToken(null);
      setUser(null);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Restore + VALIDATE the session on mount. We don't mark the user as
  // authenticated straight from localStorage — a stale/expired token would
  // then render the dashboard (or authed chrome over a public page) and only
  // bounce to /login after a failed request, which is the "flash" users see.
  // Instead we keep isLoading=true and confirm the token via /auth/me first.
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedToken) {
      setIsLoading(false);
      return;
    }

    // Make sure the validation request carries the stored token.
    api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

    let cancelled = false;
    getMe()
      .then((me) => {
        if (cancelled) return;
        const authUser = toAuthUser(me);
        setToken(storedToken);
        setUser(authUser);
        localStorage.setItem(USER_KEY, JSON.stringify(authUser));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        // A real 401 means the token is dead — the interceptor already cleared
        // storage and auth state, so leave the user logged out. For transient
        // errors (network / 5xx) fall back to the cached session rather than
        // signing a valid user out over a blip.
        if (status !== 401) {
          const cachedUser = localStorage.getItem(USER_KEY);
          if (cachedUser) {
            try {
              setToken(storedToken);
              setUser(JSON.parse(cachedUser) as AuthUser);
            } catch {
              /* ignore malformed cache */
            }
          }
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email: string, password: string) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await api.post<{
        access_token: string;
        token_type: string;
        expires_in: number;
        user: AuthUser;
      }>('/auth/login', { email, password });

      const { access_token, user: userData } = response.data;

      // Store in state
      setToken(access_token);
      setUser(userData);

      // Store in localStorage
      localStorage.setItem(TOKEN_KEY, access_token);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));

      // Set auth header for API calls
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Login failed. Please check your credentials.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // OAuth sign-in (Google / Apple). The page component obtains the provider's
  // id_token client-side; we exchange it with the backend and then store the
  // session exactly the way login() does (same keys, same state updates).
  const socialLogin = async (
    provider: 'google' | 'apple',
    idToken: string,
    fullName?: string
  ) => {
    setError(null);
    setIsLoading(true);

    try {
      const response =
        provider === 'google'
          ? await loginWithGoogle(idToken)
          : await loginWithApple(idToken, fullName);

      const { access_token, user: userData } = response;

      // Store in state
      setToken(access_token);
      setUser(userData);

      // Store in localStorage
      localStorage.setItem(TOKEN_KEY, access_token);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));

      // Set auth header for API calls
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    } catch (err: unknown) {
      // Surface backend detail (401 rejected token, 403 unverified provider
      // email, 501 provider not configured on server) to the caller.
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        `Sign in with ${provider === 'google' ? 'Google' : 'Apple'} failed. Please try again.`;
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Clear state
    setToken(null);
    setUser(null);
    setError(null);

    // Clear localStorage
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);

    // Remove auth header
    delete api.defaults.headers.common['Authorization'];
  };

  // Apply a partial update to the cached user (e.g. when the user edits
  // their own email on the Account page). Keeps state and localStorage in
  // sync so the header reflects the change without a full re-login.
  const updateUser = (patch: Partial<AuthUser>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const merged = { ...prev, ...patch } as AuthUser;
      localStorage.setItem(USER_KEY, JSON.stringify(merged));
      return merged;
    });
  };

  const refreshUser = async (): Promise<AuthUser | null> => {
    try {
      const me = await getMe();
      const fresh = toAuthUser(me);
      setUser(fresh);
      localStorage.setItem(USER_KEY, JSON.stringify(fresh));
      return fresh;
    } catch {
      return null;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isAdmin: user?.role === 'admin',
    isLoading,
    login,
    socialLogin,
    logout,
    updateUser,
    refreshUser,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
